"use client";

import { createContext, useContext } from "react";

/**
 * Factory that returns a typed `{ BrandProvider, useBrand }` pair.
 *
 * Each app instantiates this with its app-specific BrandConfig type
 * (e.g. `TuBrandConfig`, `PdfBrandConfig`) so the consumer hook is
 * strongly typed without leaking app types into shared code.
 */
export function createBrandContext<T>() {
  const Context = createContext<T | null>(null);
  Context.displayName = "BrandContext";

  function BrandProvider({
    brand,
    children,
  }: {
    brand: T;
    children: React.ReactNode;
  }) {
    return <Context.Provider value={brand}>{children}</Context.Provider>;
  }

  function useBrand(): T {
    const ctx = useContext(Context);
    if (!ctx) {
      throw new Error("useBrand must be used within a BrandProvider");
    }
    return ctx;
  }

  return { BrandProvider, useBrand };
}
